import { CreditCard, Wallet, RotateCcw, Receipt } from 'lucide-react';
import DigitalSignatureBlock from '../components/documents/DigitalSignatureBlock';

interface Props {
  data: {
    documentSubtype: 'UNIFIED_PAYMENT' | 'WALLET_TRANSACTION' | 'REFUND_ACKNOWLEDGEMENT' | 'GST_INVOICE' | 'SERVICE_FEE';
    receiptId: string;
    receiptDate: string;
    payerName: string;
    payerNhcId: string;
    payeeName: string;
    payeeNhcId?: string;
    paymentMode: string;
    transactionId?: string;
    bankRefNo?: string;
    description: string;
    amount: number;
    gstAmount?: number;
    totalAmount: number;
    status: string;
    refundReason?: string;
    walletBalance?: number;
    notes?: string[];
    signatories: { name: string; role: string; nhcId?: string; licenseNo?: string; institution?: string }[];
  };
}

function fmt(n: number) { return `Rs. ${n.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`; }

const SUBTYPE_LABELS: Record<string, { label: string; icon: typeof CreditCard }> = {
  UNIFIED_PAYMENT: { label: 'Unified Payment Receipt', icon: CreditCard },
  WALLET_TRANSACTION: { label: 'Wallet Transaction Receipt', icon: Wallet },
  REFUND_ACKNOWLEDGEMENT: { label: 'Refund Request Acknowledgement', icon: RotateCcw },
  GST_INVOICE: { label: 'GST/Tax Invoice', icon: Receipt },
  SERVICE_FEE: { label: 'NHC Service Fee Receipt', icon: Receipt },
};

export default function PaymentReceiptTemplate({ data }: Props) {
  const cfg = SUBTYPE_LABELS[data.documentSubtype] || SUBTYPE_LABELS.UNIFIED_PAYMENT;
  const Icon = cfg.icon;
  const isRefund = data.documentSubtype === 'REFUND_ACKNOWLEDGEMENT';

  return (
    <div className="space-y-2.5">
      <div className="flex items-center gap-1.5 mb-1">
        <Icon className="w-3.5 h-3.5 text-gray-700" />
        <span className="text-[9px] font-bold uppercase tracking-wider text-gray-700">{cfg.label}</span>
      </div>

      <div className="grid grid-cols-2 gap-x-4 gap-y-1 bg-gray-50 px-3 py-2 border border-gray-200 rounded text-[9px]">
        <div><span className="text-gray-500">Receipt ID:</span> <span className="font-mono text-gray-800">{data.receiptId}</span></div>
        <div><span className="text-gray-500">Date:</span> <span className="text-gray-800">{data.receiptDate}</span></div>
        <div><span className="text-gray-500">Status:</span> <span className={`font-bold ${data.status === 'COMPLETED' || data.status === 'PAID' ? 'text-green-700' : data.status === 'PENDING' ? 'text-amber-700' : 'text-gray-700'}`}>{data.status}</span></div>
        <div><span className="text-gray-500">Mode:</span> <span className="text-gray-800">{data.paymentMode}</span></div>
        {data.transactionId && <div><span className="text-gray-500">Txn ID:</span> <span className="font-mono text-gray-700">{data.transactionId}</span></div>}
        {data.bankRefNo && <div><span className="text-gray-500">Bank Ref:</span> <span className="font-mono text-gray-700">{data.bankRefNo}</span></div>}
      </div>

      <div className="grid grid-cols-2 gap-4 bg-gray-50 px-3 py-2 border border-gray-200 rounded text-[9px]">
        <div>
          <p className="text-[8px] font-bold uppercase text-gray-500 mb-0.5">{isRefund ? 'Refunded To' : 'Paid By'}</p>
          <p className="text-gray-800 font-medium">{data.payerName}</p>
          <p className="text-gray-500 font-mono text-[8px]">NHC: {data.payerNhcId}</p>
        </div>
        <div>
          <p className="text-[8px] font-bold uppercase text-gray-500 mb-0.5">{isRefund ? 'Refunded By' : 'Paid To'}</p>
          <p className="text-gray-800 font-medium">{data.payeeName}</p>
          {data.payeeNhcId && <p className="text-gray-500 font-mono text-[8px]">NHC: {data.payeeNhcId}</p>}
        </div>
      </div>

      <div className="bg-gray-50 px-3 py-1.5 border border-gray-200 rounded text-[9px]">
        <p className="text-[8px] font-bold uppercase text-gray-500 mb-0.5">Description</p>
        <p className="text-gray-800">{data.description}</p>
      </div>

      <div className="flex justify-end">
        <div className="w-52 bg-gray-50 border border-gray-200 rounded text-[9px]">
          <div className="flex justify-between py-0.5 px-2.5 border-b border-gray-200"><span className="text-gray-500">Amount</span><span className="font-mono text-gray-800">{fmt(data.amount)}</span></div>
          {(data.gstAmount ?? 0) > 0 && <div className="flex justify-between py-0.5 px-2.5 border-b border-gray-200"><span className="text-gray-500">GST</span><span className="font-mono text-gray-800">{fmt(data.gstAmount!)}</span></div>}
          <div className="flex justify-between py-1 px-2.5 bg-gray-100 font-bold">
            <span className="text-gray-800">{isRefund ? 'Refund Amount' : 'Total'}</span>
            <span className={`font-mono ${isRefund ? 'text-amber-700' : 'text-gray-900'}`}>{fmt(data.totalAmount)}</span>
          </div>
        </div>
      </div>

      {isRefund && data.refundReason && (
        <div className="bg-amber-50 border border-amber-200 rounded px-3 py-1.5 text-[9px] text-amber-800">
          <span className="font-bold">Reason:</span> {data.refundReason}
        </div>
      )}

      {data.walletBalance !== undefined && (
        <div className="bg-blue-50 border border-blue-200 rounded px-3 py-1.5 text-[9px] text-blue-800 flex items-center gap-1.5">
          <Wallet className="w-3 h-3" />
          <span>Wallet Balance: <b className="font-mono">{fmt(data.walletBalance)}</b></span>
        </div>
      )}

      {(data.documentSubtype === 'GST_INVOICE' || data.documentSubtype === 'SERVICE_FEE') && (
        <div className="bg-amber-50 border border-amber-200 rounded px-3 py-1.5 text-[8px] text-amber-800">
          Placeholder — GST/Tax invoices and service fee receipts require compliance review before production use.
        </div>
      )}

      {data.notes && data.notes.length > 0 && (
        <ul className="list-disc list-inside space-y-0.5 text-[9px] text-gray-700 bg-gray-50 px-3 py-1.5 border border-gray-200 rounded">
          {data.notes.map((n, i) => <li key={i}>{n}</li>)}
        </ul>
      )}

      <DigitalSignatureBlock signatories={data.signatories} />
    </div>
  );
}
